/**
 * Route gates (03 §5.2): everyone stands somewhere walkable, the drop band connects to every
 * objective and to the pickup without crossing mines or wire (or, for the sealed missions,
 * through exactly one breach), and the shortest valid route fits in 55 % of the clock.
 */

import { findPath } from '../../sim/astar';
import { isVehicle } from '../../sim/enemies/specials/kinds';
import { collidersFor, PROPS } from '../../sim/props';
import { RANGER, SPECIALS } from '../../sim/tuning';
import type { MissionDef, PropDef, Rect } from '../../sim/types';
import { CLOCK, VALIDATE } from '../tuning';
import { covers, type NavProbe, UNREACHABLE } from './nav';
import { firstBlock } from './vehicleRuns';

const cellOf = (nav: NavProbe, x: number, z: number): number => {
  const cx = Math.floor(x);
  const cz = Math.floor(z);
  return cx < 0 || cz < 0 || cx >= nav.width || cz >= nav.height ? -1 : cz * nav.width + cx;
};

/** How many cells with their centre in the rectangle a man can stand on: not blocked, mined or wired. */
export function standable(nav: NavProbe, rect: Rect): number {
  let count = 0;
  for (let z = Math.max(0, Math.ceil(rect.z0 - 0.5)); z + 0.5 <= rect.z1 && z < nav.height; z++) {
    for (let x = Math.max(0, Math.ceil(rect.x0 - 0.5)); x + 0.5 <= rect.x1 && x < nav.width; x++) {
      const i = z * nav.width + x;
      if (nav.full.blocked[i] === 0 && nav.soft[i] === 0) count++;
    }
  }
  return count;
}

/** Soldiers, patrol nodes, the ranger and the pickup must be on free cells and off the mines. */
export function checkStanding(def: MissionDef, nav: NavProbe): string[] {
  const out: string[] = [];
  const bad = (x: number, z: number): string | null => {
    const i = cellOf(nav, x, z);
    if (i < 0) return 'off the map';
    if (nav.full.blocked[i] !== 0) return 'on a blocked cell';
    return nav.soft[i] !== 0 ? 'in a minefield' : null;
  };
  const solids = def.props.flatMap((p) => collidersFor(p)).filter((c) => c.blocksMove);
  const emplacements = new Map(def.props.filter((p) => PROPS[p.kind].emplacement === true).map((p) => [p.id, p]));
  def.spawns.forEach((s, n) => {
    if (s.mans !== undefined) {
      // A gunner sits inside his emplacement: he only has to be in the right one.
      const prop = emplacements.get(s.mans);
      if (!prop) out.push(`spawn ${n} mans prop ${s.mans}, which is not an emplacement`);
      else if (Math.abs(prop.x - s.x) > 3 || Math.abs(prop.z - s.z) > 3) {
        out.push(`spawn ${n} is not at the emplacement he mans`);
      }
      return;
    }
    // R4: a vehicle drives, it does not walk. It starts where its route starts - on a road, on a siding, or on
    // the hardstanding of the prop it replaces - and follows that route without touching the soldiers' grid.
    // What it DOES need is a clear run: nothing steers it round a hut, so a lorry stopped in a field by a prop
    // the budget scattered later is a mission that lies to the player. That is a fault, not a shrug.
    if (isVehicle(s.kind)) {
      const route = s.route?.points.points ?? [];
      if (route.length < 2 && s.route !== undefined) out.push(`the ${s.kind} at ${s.x}, ${s.z} has a route of one point`);
      for (const [x, z] of route) {
        if (cellOf(nav, x, z) < 0) out.push(`a route node of spawn ${n} is off the map at ${x}, ${z}`);
      }
      const stopped = firstBlock(def, s, route);
      if (stopped) out.push(`the ${s.kind}'s run is blocked at ${stopped.x}, ${stopped.z} by prop ${stopped.propId}`);
      return;
    }
    if (s.kind === 'miniBunker') {
      // The sim blocks the cells under a mini-bunker's body itself: it only has to be clear of everything else.
      const hit = solids.find((c) => covers(c, s.x, s.z, SPECIALS.bunkerBody));
      if (hit) out.push(`the mini-bunker at ${s.x}, ${s.z} stands inside prop ${hit.propId}`);
      return;
    }
    const why = bad(s.x, s.z);
    if (why) out.push(`spawn ${n} (${s.kind}) is ${why} at ${s.x}, ${s.z}`);
    const home = nav.component[cellOf(nav, s.x, s.z)] ?? -1;
    for (const [x, z] of s.patrol ?? []) {
      const nodeWhy = bad(x, z);
      if (nodeWhy) out.push(`a patrol node of spawn ${n} is ${nodeWhy} at ${x}, ${z}`);
      else if ((nav.component[cellOf(nav, x, z)] ?? -2) !== home) {
        out.push(`a patrol node of spawn ${n} at ${x}, ${z} cannot be walked to`);
      }
    }
  });
  const pickup = bad(def.pickup.x, def.pickup.z);
  if (pickup) out.push(`the pickup is ${pickup}`);
  // Alternate LZ (04 §8): the second X answers to every rule the first does. A point he cannot stand on,
  // or cannot walk to from the first, is the card's old lie with coordinates attached.
  const alt = def.pickupAlt;
  if (alt !== undefined) {
    const why = bad(alt.x, alt.z);
    if (why) out.push(`the alternate pickup is ${why}`);
    else if ((nav.component[cellOf(nav, alt.x, alt.z)] ?? -1) !== (nav.component[cellOf(nav, def.pickup.x, def.pickup.z)] ?? -2)) {
      out.push(`the alternate pickup at ${alt.x}, ${alt.z} cannot be walked to from the pickup`);
    } else if (alt.z >= def.dropBand.z0) out.push('the alternate pickup must be north of the drop band');
  }
  const player = bad(def.player.x, def.player.z);
  if (player) out.push(`the ranger starts ${player}`);
  // A captured start (10 §2, mission 32) is wherever the cell is: `checkCaptured` holds that to its own rules.
  if (def.startCaptured !== true && (def.player.z < def.dropBand.z0 || def.player.z > def.dropBand.z1)) {
    out.push('the ranger starts outside the drop band');
  }
  return out;
}

/** Cheapest soft-cell count to get beside a prop, and the cell that achieves it nearest to `from`. */
function beside(nav: NavProbe, prop: PropDef, fromX: number, fromZ: number): { cost: number; cell: number } {
  const spec = PROPS[prop.kind];
  // Half-extents of the footprint: a run's length, or the furthest any of the prop's shapes reaches.
  const reachX = Math.max(0, ...spec.shapes.map((s) => (s.r ?? s.hx ?? 0) + Math.abs(s.dx ?? 0)));
  const reachZ = Math.max(0, ...spec.shapes.map((s) => (s.r ?? s.hz ?? 0) + Math.abs(s.dz ?? 0)));
  const hx = spec.runHalfDepth !== undefined ? (prop.length ?? 4) / 2 : reachX;
  const hz = spec.runHalfDepth ?? reachZ;
  const reach = VALIDATE.vicinity + RANGER.radius;
  let best = { cost: UNREACHABLE, cell: -1 };
  let bestD = Infinity;
  for (let z = Math.floor(prop.z - hz - reach); z <= Math.floor(prop.z + hz + reach); z++) {
    for (let x = Math.floor(prop.x - hx - reach); x <= Math.floor(prop.x + hx + reach); x++) {
      const i = cellOf(nav, x + 0.5, z + 0.5);
      if (i < 0 || nav.hard[i] !== 0) continue;
      const cost = nav.cost[i] ?? UNREACHABLE;
      const d = (x + 0.5 - fromX) * (x + 0.5 - fromX) + (z + 0.5 - fromZ) * (z + 0.5 - fromZ);
      if (cost < best.cost || (cost === best.cost && d < bestD)) {
        best = { cost, cell: i };
        bestD = d;
      }
    }
  }
  return best;
}

/** The easiest cell of a zone to get to: fewest soft cells, then nearest to `from`. */
function within(nav: NavProbe, zone: Rect, fromX: number, fromZ: number): { cost: number; cell: number } {
  let best = { cost: UNREACHABLE, cell: -1 };
  let bestD = Infinity;
  for (let z = Math.max(0, Math.ceil(zone.z0)); z < Math.min(nav.height, Math.floor(zone.z1)); z++) {
    for (let x = Math.max(0, Math.ceil(zone.x0)); x < Math.min(nav.width, Math.floor(zone.x1)); x++) {
      const i = z * nav.width + x;
      const cost = nav.hard[i] === 0 ? (nav.cost[i] ?? UNREACHABLE) : UNREACHABLE;
      const d = (x + 0.5 - fromX) * (x + 0.5 - fromX) + (z + 0.5 - fromZ) * (z + 0.5 - fromZ);
      if (cost < best.cost || (cost === best.cost && cost !== UNREACHABLE && d < bestD)) {
        best = { cost, cell: i };
        bestD = d;
      }
    }
  }
  return best;
}

interface Waypoint {
  prop?: PropDef;
  zone?: Rect;
  x: number;
  z: number;
  name: string;
}

/** Everything the ranger has to get to: target props, the officer, or the zone to reach. */
function waypoints(def: MissionDef): Waypoint[] {
  const out: Waypoint[] = [];
  for (const id of def.objective.targetPropIds) {
    const prop = def.props.find((p) => p.id === id);
    if (prop) out.push({ prop, x: prop.x, z: prop.z, name: `${prop.kind} ${id}` });
  }
  for (const s of def.spawns) if (s.captureTarget === true) out.push({ x: s.x, z: s.z, name: 'the officer' });
  const zone = def.objective.zone;
  if (zone && def.objective.targetPropIds.length === 0) {
    out.push({ zone, x: (zone.x0 + zone.x1) / 2, z: (zone.z0 + zone.z1) / 2, name: 'the objective zone' });
  }
  return out;
}

export interface Connectivity {
  out: string[];
  /** Soft cells crossed from the pickup to each target prop; -1 when it cannot be reached at all. */
  breach: Map<number, number>;
}

export function checkConnectivity(def: MissionDef, nav: NavProbe, allowed: { min: number; max: number }): Connectivity {
  const out: string[] = [];
  const breach = new Map<number, number>();
  let open = 0;
  let connected = 0;
  for (let z = Math.ceil(def.dropBand.z0); z < Math.floor(def.dropBand.z1) && z < nav.height; z++) {
    for (let x = 0; x < nav.width; x++) {
      const i = z * nav.width + x;
      if (nav.hard[i] !== 0 || nav.soft[i] !== 0) continue;
      open++;
      if (nav.cost[i] === 0) connected++;
    }
  }
  if (open === 0 || connected / open < VALIDATE.dropBandConnected) {
    const share = open === 0 ? 0 : Math.round((connected / open) * 100);
    out.push(`only ${share} % of the drop band connects to the pickup without crossing mines or wire`);
  }
  for (const w of waypoints(def)) {
    let cost = UNREACHABLE;
    if (w.prop) cost = beside(nav, w.prop, def.pickup.x, def.pickup.z).cost;
    else if (w.zone) cost = within(nav, w.zone, def.pickup.x, def.pickup.z).cost;
    else {
      const i = cellOf(nav, w.x, w.z);
      cost = i >= 0 ? (nav.cost[i] ?? UNREACHABLE) : UNREACHABLE;
    }
    if (w.prop) breach.set(w.prop.id, cost === UNREACHABLE ? -1 : cost);
    if (cost === UNREACHABLE) {
      out.push(`${w.name} cannot be reached from the pickup`);
    } else if (cost > allowed.max) {
      out.push(`${w.name} is behind ${cost} m of mines or wire (at most ${allowed.max} allowed)`);
    } else if (cost < allowed.min && w.prop) {
      out.push(`${w.name} should be sealed off but can be walked to`);
    }
  }
  return { out, breach };
}

/** Start -> every objective (nearest first) -> pickup, at walking pace, against 55 % of the clock. */
export function checkClock(def: MissionDef, nav: NavProbe): string[] {
  const grid = { width: nav.width, height: nav.height, blocked: nav.hard };
  const todo = waypoints(def);
  let x = def.player.x;
  let z = def.player.z;
  let metres = 0;
  const leg = (toCell: number, name: string): string | null => {
    const [gx, gz] = [toCell % nav.width, Math.floor(toCell / nav.width)];
    const path = toCell < 0 ? null : findPath(grid, Math.floor(x), Math.floor(z), gx, gz, 200000);
    if (!path) return `no route from ${Math.floor(x)}, ${Math.floor(z)} to ${name}`;
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      if (a && b) metres += a[0] !== b[0] && a[1] !== b[1] ? Math.SQRT2 : 1;
    }
    const end = path[path.length - 1];
    if (end) {
      x = end[0] + 0.5;
      z = end[1] + 0.5;
    }
    return null;
  };
  while (todo.length > 0) {
    const away = (w: Waypoint): number => (w.x - x) * (w.x - x) + (w.z - z) * (w.z - z);
    todo.sort((a, b) => away(a) - away(b));
    const next = todo.shift();
    if (!next) break;
    const cell = next.prop ? beside(nav, next.prop, x, z).cell
      : next.zone ? within(nav, next.zone, x, z).cell
        : cellOf(nav, next.x, next.z);
    const failed = leg(cell, next.name);
    if (failed) return [failed];
  }
  const home = leg(cellOf(nav, def.pickup.x, def.pickup.z), 'the pickup');
  if (home) return [home];
  const seconds = metres / RANGER.walkSpeed;
  const limit = def.clockSeconds * CLOCK.feasibleFraction;
  if (seconds <= limit) return [];
  return [`the shortest route takes ${Math.round(seconds)} s at a walk; the clock allows ${Math.round(limit)} s`];
}
